import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { eventTypeBadgeOptions } from 'src/features/eventLog/utils/constants';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import { StatisticEvent } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { Badge } from 'src/registry/ui/Badge';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { PATH } from 'src/features/navigation/utils/constants';
import ViewAllButton from 'src/components/ViewAllButton';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';

type Props = {
    data?: StatisticEvent[];
    totalEvents?: number;
    isError?: boolean;
};
export default function WidgetEvents({ data, totalEvents, isError }: Props) {
    const { formatMessage, locale } = useIntl();
    const permissionAccessEventLogs = useHasPermissions([PERMISSION.ACCESS_EVENT_LOGS]);

    const columns: ColumnDef<StatisticEvent>[] = [
        {
            accessorKey: 'timestamp',
            header: formatMessage(homeMessages.time),
            cell: (info) =>
                new Date(info.getValue() as string).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                }),
            maxSize: 60,
        },
        {
            accessorKey: 'event_type',
            header: formatMessage(homeMessages.type),
            cell: (info) => {
                const rawType = info.getValue() as EVENT_TYPE;
                const option = eventTypeBadgeOptions[rawType];
                const { iconColor } = option?.color ?? { iconColor: undefined };

                return (
                    <Badge
                        theme={option?.theme || 'gray'}
                        className="flex w-full min-w-0 items-center justify-start gap-1"
                    >
                        {option?.icon && (
                            <FontAwesomeIcon
                                icon={option.icon}
                                className={cn('z-0', !iconColor && !option.theme && 'opacity-50')}
                                style={{ color: iconColor }}
                                size={option.iconSize}
                            />
                        )}
                        <span className="min-w-0 truncate">
                            {option ? formatMessage(option.label) : rawType}
                        </span>
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'event_data',
            header: formatMessage(homeMessages.identifier),
            cell: (info) => {
                const { identifiers, visitor } = info.row.original.event_data;
                const visitorId = Array.isArray(visitor) ? visitor[0]?.id : visitor?.id;
                const value = identifiers?.[0]?.value ?? '-';

                if (!visitorId || value === '-') return value;

                return (
                    <Link
                        to={`/${PATH.VISITORS}/visitor/${visitorId}`}
                        onClick={(e) => e.stopPropagation()}
                        className={cn('text-primary-700 hover:text-primary-600 cursor-pointer font-semibold')}
                    >
                        {value}
                    </Link>
                );
            },
        },
    ];

    return (
        <GeneralBlock className="min-w-0 flex-1 gap-6">
            <div className="flex items-start justify-between">
                <div className="flex gap-6">
                    <div className="flex size-14 items-center justify-center rounded-full border-8 border-green-50 bg-green-100 p-1 text-2xl text-green-700">
                        <FontAwesomeIcon icon={ICON_MAP.TREND_UP} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-600">
                            {formatMessage(homeMessages.totalEventsToday)}
                        </span>
                        <span className="text-lg font-bold">{isError ? '-' : totalEvents}</span>
                    </div>
                </div>
                {permissionAccessEventLogs && <ViewAllButton route={`/${PATH.VISITORS}/event-log`} />}
            </div>
            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                // Keep the table within the widget: when a side panel opens the grid track shrinks and the
                // full-width table no longer fits, so allow horizontal scrolling instead of letting the
                // identifier value spill outside the container.
                <div className="w-full overflow-x-auto">
                    <StatisticTable
                        className="w-full"
                        title={formatMessage(homeMessages.lastTenEvents)}
                        data={data}
                        columns={columns as any}
                    />
                </div>
            )}
        </GeneralBlock>
    );
}
